import { watchEffect } from "vue";
import { Channel, Currency, SessionData } from "@smeetz/orders";
import POSConfigModule from "@/store/modules/pos-config";
import AuthModule from "@/store/modules/auth";
import { SharedCallbacks } from "@/utils/shared-resources";
import CashFloatModule from "@/store/modules/cash-float";
import DeviceModule from "@/store/modules/device";

/**
 * initializeOrderSessionDataService returns a Promise that resolves only
 * when the session data is actually available.
 */

let sessionDataInstance: SessionData | null = null;
let resolveSessionDataPromise:
  | ((value: void | PromiseLike<void>) => void)
  | null = null;

const sessionDataPromise = new Promise<void>((resolve) => {
  resolveSessionDataPromise = resolve;
});

/**
 * Names of the prerequisites still missing on the last watchEffect run.
 * Drives the stuck-initialization diagnostic and the getOrderSessionData
 * error message, so a missing prerequisite is visible in the logs instead
 * of silently never resolving the readiness promise.
 */
let missingPrerequisites: string[] = [];

export function initializeOrderSessionDataService(): Promise<void> {
  watchEffect(() => {
    const groupInfo = POSConfigModule?.groupInfo;
    const deviceUid = DeviceModule?.deviceUid;
    const selectedConfig = POSConfigModule?.selectedConfig;
    const salesSessionId = CashFloatModule.cashFloat?.uid;

    let cognitoId: string | undefined;
    try {
      cognitoId = AuthModule?.cognitoId;
    } catch {
      cognitoId = undefined;
    }

    let sellerId: number | undefined;
    try {
      sellerId = SharedCallbacks?.getSellerId?.();
    } catch {
      sellerId = undefined;
    }

    missingPrerequisites = Object.entries({
      groupId: groupInfo?.id,
      salesConfigId: selectedConfig?.id,
      sellerId,
      cognitoId,
      salesSessionId,
    })
      .filter(([, value]) => !value)
      .map(([name]) => name);

    if (
      groupInfo?.id &&
      selectedConfig?.id &&
      sellerId &&
      cognitoId &&
      salesSessionId
    ) {
      sessionDataInstance = {
        channel: Channel.SMEETZ_POS,
        currency: Currency[groupInfo.currency as Currency] || Currency.CHF,
        groupId: Number(groupInfo.id),
        sellerId,
        sellerCogn: cognitoId,
        salesSessionId,
        salesConfigId: String(selectedConfig.id),
        deviceUid: deviceUid || undefined,
      };

      if (resolveSessionDataPromise) {
        resolveSessionDataPromise();
        resolveSessionDataPromise = null; // Ensure it only resolves once
      }
    }
  });

  return sessionDataPromise;
}

/**
 * Returns the current sessionData snapshot (rebuilt by the watcher whenever a
 * prerequisite changes). Throws — naming the missing prerequisites — if the
 * service has not produced one yet.
 */
export function getOrderSessionData(): SessionData {
  if (!sessionDataInstance) {
    throw new Error(
      `SessionDataService has not been initialized. Call initializeOrderSessionDataService() first. Missing prerequisites: ${
        missingPrerequisites.join(", ") || "none recorded"
      }`
    );
  }
  return sessionDataInstance;
}
